// Microservice API configuration from environment variables
const MICROSERVICES = {
  AUTH: import.meta.env.VITE_AUTH_SERVICE_URL || 'http://localhost:3000',
  PROJECTS: import.meta.env.VITE_PROJECTS_SERVICE_URL || 'http://localhost:3001',
  // Note: TENANTS, ROLES, and PERMISSIONS are handled by AUTH service
} as const

export interface ApiResponse<T = any> {
  success: boolean
  message: string
  data?: T
  error?: string
}

export interface ApiError {
  message: string
  status?: number
  data?: any
}

class ApiClient {
  private getBaseURL(service: keyof typeof MICROSERVICES): string {
    return MICROSERVICES[service]
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false,
    service: keyof typeof MICROSERVICES = 'AUTH'
  ): Promise<ApiResponse<T>> {
    const baseURL = this.getBaseURL(service)
    const url = `${baseURL}${endpoint}`
    
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Add authorization header if token exists
    const token = localStorage.getItem('accessToken')
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    }

    try {
      const response = await fetch(url, config)
      const data = await response.json()

      // Handle 401 Unauthorized
      if (!response.ok && response.status === 401 && !isRetry) {
        // Check if token has been invalidated (force logout)
        const message = Array.isArray(data.message) ? data.message[0] : data.message
        const isTokenInvalidated = message?.includes('invalidated') || message?.includes('revoked')
        
        if (isTokenInvalidated) {
          console.log('Token has been invalidated, logging out user...')
          // Import auth store dynamically to avoid circular dependency
          const { useAuthStore } = await import('../stores/authStore')
          await useAuthStore.getState().logout()
          window.location.href = '/login'
          throw {
            message: 'Your session has been terminated. Please login again.',
            status: 401,
            data: data,
          } as ApiError
        }
        
        // Otherwise, try to refresh token (expired token case)
        console.log('Access token expired, attempting to refresh...')
        
        try {
          // Import auth store dynamically to avoid circular dependency
          const { useAuthStore } = await import('../stores/authStore')
          await useAuthStore.getState().refreshToken()
          
          // Retry the original request with new token
          return this.request<T>(endpoint, options, true, service)
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError)
          // If refresh fails, redirect to login
          window.location.href = '/login'
          throw {
            message: 'Session expired. Please login again.',
            status: 401,
            data: data,
          } as ApiError
        }
      }

      if (!response.ok) {
        throw {
          message: data.message || 'An error occurred',
          status: response.status,
          data: data,
        } as ApiError
      }

      return data
    } catch (error) {
      if (error instanceof TypeError) {
        // Network error
        throw {
          message: 'Network error. Please check your connection.',
          status: 0,
        } as ApiError
      }
      throw error
    }
  }

  async get<T>(endpoint: string, options?: RequestInit, service: keyof typeof MICROSERVICES = 'AUTH'): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' }, false, service)
  }

  async post<T>(endpoint: string, data?: any, options?: RequestInit, service: keyof typeof MICROSERVICES = 'AUTH'): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }, false, service)
  }

  async put<T>(endpoint: string, data?: any, options?: RequestInit, service: keyof typeof MICROSERVICES = 'AUTH'): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }, false, service)
  }

  async patch<T>(endpoint: string, data?: any, options?: RequestInit, service: keyof typeof MICROSERVICES = 'AUTH'): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }, false, service)
  }

  async delete<T>(endpoint: string, options?: RequestInit, service: keyof typeof MICROSERVICES = 'AUTH'): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' }, false, service)
  }

  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    options: RequestInit = {},
    service: keyof typeof MICROSERVICES = 'AUTH',
    isRetry: boolean = false
  ): Promise<ApiResponse<T>> {
    const baseURL = this.getBaseURL(service)
    const url = `${baseURL}${endpoint}`
    
    const headers: Record<string, string> = {}

    // Add authorization header if token exists
    const token = localStorage.getItem('accessToken')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const config: RequestInit = {
      ...options,
      method: 'POST',
      headers: {
        ...headers,
        ...options.headers,
      },
      body: formData,
    }

    try {
      const response = await fetch(url, config)
      const data = await response.json()

      // Handle 401 Unauthorized
      if (!response.ok && response.status === 401 && !isRetry) {
        // Check if token has been invalidated (force logout)
        const message = Array.isArray(data.message) ? data.message[0] : data.message
        const isTokenInvalidated = message?.includes('invalidated') || message?.includes('revoked')
        
        if (isTokenInvalidated) {
          console.log('Token has been invalidated, logging out user...')
          // Import auth store dynamically to avoid circular dependency
          const { useAuthStore } = await import('../stores/authStore')
          await useAuthStore.getState().logout()
          window.location.href = '/login'
          throw {
            message: 'Your session has been terminated. Please login again.',
            status: 401,
            data: data,
          } as ApiError
        }
        
        // Otherwise, try to refresh token (expired token case)
        console.log('Access token expired, attempting to refresh...')
        
        try {
          // Import auth store dynamically to avoid circular dependency
          const { useAuthStore } = await import('../stores/authStore')
          await useAuthStore.getState().refreshToken()
          
          // Retry the original request with new token
          return this.postFormData<T>(endpoint, formData, options, service, true)
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError)
          // If refresh fails, redirect to login
          window.location.href = '/login'
          throw {
            message: 'Session expired. Please login again.',
            status: 401,
            data: data,
          } as ApiError
        }
      }

      if (!response.ok) {
        throw {
          message: data.message || 'An error occurred',
          status: response.status,
          data: data,
        } as ApiError
      }

      return data
    } catch (error) {
      if (error instanceof TypeError) {
        // Network error
        throw {
          message: 'Network error. Please check your connection.',
          status: 0,
        } as ApiError
      }
      throw error
    }
  }
}

export const apiClient = new ApiClient()
export default apiClient